import cx from 'classnames'
import { Formik, useFormikContext } from 'formik'
import { BrandAwarenessPlanProps } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Types'
import { useUpdateBrandPlan, Variables } from 'hooks/queries/useUpdateBrandPlan'
import Styles from './BrandPlanAwareness.module.scss'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight, Mailchimp, TwitterBrand } from 'blaze-ui/icons'
import { BrandPlan, BrandPlanTactic } from 'types'
import { useCallback, useMemo, useState } from 'react'
import { Form } from 'components/FirstTimeExperienceModal/components/Form/Form'
import { ContentsCount } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/ContentsCount'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { FacebookBrand, WordPressBrand, GoHighLevelBrand, WixBrand, ShopifyBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { ChevronDown, ChevronUp, TikTokBrand, YouTubeBrand } from 'blaze-ui/icons/20'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { ChannelType } from 'components/CampaignCreationModal/Types'

import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'

import { Topics } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Topics'
import { SOCIAL_MEDIA_TACTICS } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'

import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'

const BrandPlanAwareness = ({
  brandPlan,
  onBack,
  onNext,
  selectedBlogPlatform,
  selectedEmailPlatform,
}: BrandAwarenessPlanProps) => {
  const { isMobile } = useResponsiveDesign()
  const { submitForm, values } = useFormikContext<BrandPlan>()
  const {
    seo_contents,
    email_contents,
    instagram_contents,
    facebook_contents,
    twitter_contents,
    linkedin_contents,
    tiktok_contents,
    youtube_contents,
  } = values
  const { tactics } = brandPlan
  const { trackTopicsReviewed } = useOnboardingTracking()
  const [showingSocialMediaContents, setShowSocialMediaContents] = useState(!isMobile)

  // Blog platform selection logic - same as Seo component
  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const shopifyEnabled = useFlipperFlag('shopify_integration')
  const { data: integrations } = useExternalIntegrations()

  const selectedBlogPlatformBranding = getBlogPlatformBrandingWithOnboardingSelection({
    channel: ChannelType.BlogPost,
    externalIntegrations: integrations?.external_integrations,
    selectedBlogPlatform: selectedBlogPlatform || undefined,
    featureFlags: {
      goHighLevelEnabled,
      wixEnabled,
      blogPlatformOnboardingBrandingEnabled: true,
      shopifyEnabled,
    },
  })

  const getBlogIcon = () => {
    if (selectedBlogPlatform === 'generic') {
      return () => <ChannelIcon size={20} channel={ChannelType.BlogPost} />
    }

    switch (selectedBlogPlatformBranding) {
      case ExternalIntegrationEnum.GoHighLevel:
        return GoHighLevelBrand
      case ExternalIntegrationEnum.Wix:
        return WixBrand
      case ExternalIntegrationEnum.WordPress:
        return WordPressBrand
      case ExternalIntegrationEnum.Shopify:
        return ShopifyBrand
      default:
        return () => <ChannelIcon size={20} channel={ChannelType.BlogPost} />
    }
  }

  const getEmailIcon = () => {
    if (selectedEmailPlatform === 'generic') {
      return () => <ChannelIcon size={20} channel={ChannelType.Newsletter} />
    }
    return Mailchimp
  }

  const hasSocialMedia = useMemo(() => SOCIAL_MEDIA_TACTICS.some((tactic) => tactics.includes(tactic)), [tactics])

  const totalSocialMediaPosts = useMemo(() => {
    let total = 0

    if (tactics.includes(BrandPlanTactic.Instagram)) {
      total += instagram_contents.reduce((sum, item) => sum + item.count, 0)
    }
    if (tactics.includes(BrandPlanTactic.Facebook)) {
      total += facebook_contents.reduce((sum, item) => sum + item.count, 0)
    }
    if (tactics.includes(BrandPlanTactic.Twitter)) {
      total += twitter_contents.reduce((sum, item) => sum + item.count, 0)
    }
    if (tactics.includes(BrandPlanTactic.LinkedIn)) {
      total += linkedin_contents.reduce((sum, item) => sum + item.count, 0)
    }
    if (tactics.includes(BrandPlanTactic.TikTok)) {
      total += tiktok_contents.reduce((sum, item) => sum + item.count, 0)
    }
    if (tactics.includes(BrandPlanTactic.YouTube)) {
      total += youtube_contents.reduce((sum, item) => sum + item.count, 0)
    }

    return total
  }, [
    tactics,
    instagram_contents,
    facebook_contents,
    twitter_contents,
    linkedin_contents,
    tiktok_contents,
    youtube_contents,
  ])

  const minSocialTopics = useMemo(() => {
    const instagramCount = instagram_contents.reduce((sum, item) => sum + item.count, 0)
    const facebookCount = facebook_contents.reduce((sum, item) => sum + item.count, 0)
    const twitterCount = twitter_contents.reduce((sum, item) => sum + item.count, 0)
    const linkedinCount = linkedin_contents.reduce((sum, item) => sum + item.count, 0)
    const tiktokCount = tiktok_contents.reduce((sum, item) => sum + item.count, 0)
    const youtubeCount = youtube_contents.reduce((sum, item) => sum + item.count, 0)

    const counts = []

    if (tactics.includes(BrandPlanTactic.Instagram)) {
      counts.push(instagramCount)
    }
    if (tactics.includes(BrandPlanTactic.Facebook)) {
      counts.push(facebookCount)
    }
    if (tactics.includes(BrandPlanTactic.Twitter)) {
      counts.push(twitterCount)
    }
    if (tactics.includes(BrandPlanTactic.LinkedIn)) {
      counts.push(linkedinCount)
    }
    if (tactics.includes(BrandPlanTactic.TikTok)) {
      counts.push(tiktokCount)
    }
    if (tactics.includes(BrandPlanTactic.YouTube)) {
      counts.push(youtubeCount)
    }

    return Math.max(...counts)
  }, [
    tactics,
    instagram_contents,
    facebook_contents,
    twitter_contents,
    linkedin_contents,
    tiktok_contents,
    youtube_contents,
  ])

  return (
    <Form className={Styles.form}>
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                <Button
                  id="brand-plan-awareness-back"
                  variant="tertiary"
                  size="xl"
                  onPress={onBack}
                  frontIcon={ArrowLeft}
                  aria-label="Back"
                >
                  Back
                </Button>
              </ResponsiveDesign.Desktop>
              <Button
                id="brand-plan-awareness-next"
                size="xl"
                onPress={async () => {
                  trackTopicsReviewed()
                  await submitForm()
                  onNext()
                }}
                endIcon={ArrowRight}
                aria-label="Looks Good"
                fullWidth={isMobile}
              >
                Looks Good
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        {hasSocialMedia && (
          <div className={Styles.section}>
            <div className={Styles.leftContent}>
              <div className={Styles.socialMediaHeader}>
                <Heading level={4}>Social Media</Heading>
                <ResponsiveDesign.Mobile>
                  {showingSocialMediaContents && (
                    <Button
                      variant="editor"
                      endIcon={ChevronUp}
                      size="sm"
                      onPress={() => setShowSocialMediaContents(false)}
                    >
                      Show Less
                    </Button>
                  )}
                </ResponsiveDesign.Mobile>
              </div>
              <div className={Styles.contents}>
                <ResponsiveDesign.Mobile>
                  {!showingSocialMediaContents && (
                    <div className={Styles.collapsedTactics}>
                      <div className={Styles.icons}>
                        {brandPlan.tactics.includes(BrandPlanTactic.Facebook) && (
                          <div className={cx(Styles.icon, Styles[BrandPlanTactic.Facebook])}>
                            <FacebookBrand size={35} />
                          </div>
                        )}
                        {brandPlan.tactics.includes(BrandPlanTactic.LinkedIn) && (
                          <div className={cx(Styles.icon, Styles[BrandPlanTactic.LinkedIn])}>
                            <LinkedInBrand size={33} />
                          </div>
                        )}
                        {brandPlan.tactics.includes(BrandPlanTactic.Twitter) && (
                          <div className={cx(Styles.icon, Styles[BrandPlanTactic.Twitter])}>
                            <TwitterBrand size={33} />
                          </div>
                        )}
                        {brandPlan.tactics.includes(BrandPlanTactic.Instagram) && (
                          <div className={cx(Styles.icon, Styles[BrandPlanTactic.Instagram])}>
                            <InstagramBrand size={33} />
                          </div>
                        )}
                        {brandPlan.tactics.includes(BrandPlanTactic.TikTok) && (
                          <div className={cx(Styles.icon, Styles[BrandPlanTactic.TikTok])}>
                            <TikTokBrand size={33} />
                          </div>
                        )}
                        {brandPlan.tactics.includes(BrandPlanTactic.YouTube) && (
                          <div className={cx(Styles.icon, Styles[BrandPlanTactic.YouTube])}>
                            <YouTubeBrand size={33} />
                          </div>
                        )}
                      </div>
                      <Button
                        variant="editor"
                        endIcon={ChevronDown}
                        size="sm"
                        onPress={() => setShowSocialMediaContents(true)}
                      >
                        {totalSocialMediaPosts} posts per week
                      </Button>
                    </div>
                  )}
                </ResponsiveDesign.Mobile>
                {showingSocialMediaContents && brandPlan.tactics.includes(BrandPlanTactic.Instagram) && (
                  <ContentsCount name="instagram_contents" icon={InstagramBrand} />
                )}
                {showingSocialMediaContents && brandPlan.tactics.includes(BrandPlanTactic.Facebook) && (
                  <ContentsCount name="facebook_contents" icon={FacebookBrand} />
                )}
                {showingSocialMediaContents && brandPlan.tactics.includes(BrandPlanTactic.Twitter) && (
                  <ContentsCount name="twitter_contents" icon={TwitterBrand} />
                )}
                {showingSocialMediaContents && brandPlan.tactics.includes(BrandPlanTactic.LinkedIn) && (
                  <ContentsCount name="linkedin_contents" icon={LinkedInBrand} />
                )}
                {showingSocialMediaContents && brandPlan.tactics.includes(BrandPlanTactic.TikTok) && (
                  <ContentsCount name="tiktok_contents" icon={TikTokBrand} />
                )}
                {showingSocialMediaContents && brandPlan.tactics.includes(BrandPlanTactic.YouTube) && (
                  <ContentsCount name="youtube_contents" icon={YouTubeBrand} />
                )}
              </div>
            </div>
            <div className={Styles.rightContent}>
              <Topics name="social_topics" minValue={minSocialTopics} />
            </div>
          </div>
        )}
        {brandPlan.tactics.includes(BrandPlanTactic.SEO) && (
          <div className={Styles.section}>
            <div className={Styles.leftContent}>
              <Heading level={3}>Blog</Heading>
              <div className={Styles.contents}>
                <ContentsCount name="seo_contents" icon={getBlogIcon()} />
              </div>
            </div>
            <div className={Styles.rightContent}>
              <Topics name="seo_topics" minValue={seo_contents.reduce((sum, item) => sum + item.count, 0)} />
            </div>
          </div>
        )}
        {brandPlan.tactics.includes(BrandPlanTactic.Email) && (
          <div className={Styles.section}>
            <div className={Styles.leftContent}>
              <Heading level={3}>Emails</Heading>
              <div className={Styles.contents}>
                <ContentsCount name="email_contents" icon={getEmailIcon()} />
              </div>
            </div>
            <div className={Styles.rightContent}>
              <Topics name="email_topics" minValue={email_contents.reduce((sum, item) => sum + item.count, 0)} />
            </div>
          </div>
        )}
      </StickyFooterProvider>
    </Form>
  )
}

const BrandAwarenessPlanFormikContainer = ({ brandPlan, onNext, ...rest }: BrandAwarenessPlanProps) => {
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlan.id })

  const initialValues = useMemo(() => {
    const {
      facebook_contents,
      facebook_topics,
      instagram_contents,
      instagram_topics,
      twitter_contents,
      twitter_topics,
      linkedin_topics,
      linkedin_contents,
      tiktok_contents,
      tiktok_topics,
      youtube_contents,
      youtube_topics,
      seo_contents,
      seo_topics,
      email_contents,
      email_topics,
    } = brandPlan
    return {
      social_topics: [
        ...(facebook_topics ?? []),
        ...(instagram_topics ?? []),
        ...(linkedin_topics ?? []),
        ...(twitter_topics ?? []),
        ...(tiktok_topics ?? []),
        ...(youtube_topics ?? []),
      ],
      facebook_contents,
      instagram_contents,
      twitter_contents,
      linkedin_contents,
      tiktok_contents,
      youtube_contents,
      seo_contents,
      seo_topics,
      email_contents,
      email_topics,
    }
  }, [brandPlan])

  const handleSubmit = useCallback(
    ({
      social_topics,
      ...rest
    }: Partial<Omit<Variables, 'facebook_topics' | 'instagram_topics' | 'twitter_topics' | 'linkedin_topics'>> & {
      social_topics: string[]
    }) => {
      // we no longer separate topics by social channel, just use instagram for everything
      return updateBrandPlan({
        ...rest,
        instagram_topics: social_topics,
        facebook_topics: [],
        linkedin_topics: [],
        twitter_topics: [],
        tiktok_topics: [],
        youtube_topics: [],
      })
    },
    [updateBrandPlan],
  )

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues} enableReinitialize>
      <BrandPlanAwareness brandPlan={brandPlan} onNext={onNext} {...rest} />
    </Formik>
  )
}

export { BrandAwarenessPlanFormikContainer as BrandAwarenessPlan }
